// backup/pages/Home/index.jsx
import React, { useState, useEffect } from 'react';
import { Card, Typography, Tag, Button, Space, List, Skeleton, Empty, message } from 'antd';
import { CalendarOutlined, UserOutlined, ClockCircleOutlined } from '@ant-design/icons';
import AppFooter from '../../components/Footer'; 
import AppHeader from '../../components/Header';
import { useUser } from '../../utils/userContext';
import './index.css';

const { Title, Text } = Typography;

const Home = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, logout } = useUser();

  useEffect(() => {
    // 从 localStorage 加载文章数据
    const loadArticles = () => {
      try {
        const storedArticles = JSON.parse(localStorage.getItem('articles')) || [];
        // 只显示最新的 6 篇文章
        const latestArticles = storedArticles.slice(0, 6);
        setArticles(latestArticles);
      } catch (error) {
        console.error('加载文章数据失败:', error);
        message.error('加载文章数据失败');
        setArticles([]);
      } finally {
        setLoading(false);
      }
    };

    loadArticles();

    // 监听 localStorage 变化
    const handleStorageChange = () => {
      try {
        const storedArticles = JSON.parse(localStorage.getItem('articles')) || [];
        const latestArticles = storedArticles.slice(0, 6);
        setArticles(latestArticles);
      } catch (error) {
        console.error('更新文章数据失败:', error);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const handleViewArticle = (article) => {
    // 跳转到文章详情页
    window.location.href = `/archive#${article.id}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // 计算阅读时间
  const calculateReadingTime = (content) => {
    const wordsPerMinute = 200; // 假设每分钟阅读200字
    const wordCount = content ? content.length : 0;
    const minutes = Math.ceil(wordCount / wordsPerMinute);
    return minutes > 0 ? minutes : 1;
  };

  // 获取热门文章（按内容长度排序）
  const getHotArticles = () => {
    return [...articles]
      .sort((a, b) => b.content.length - a.content.length)
      .slice(0, 5);
  };

  // 获取所有标签
  const getAllTags = () => {
    const allTags = articles.flatMap(article => article.tags || []);
    return [...new Set(allTags)];
  };

  return (
    <div className="home-container">
      <AppHeader 
        loggedInUser={user?.username} 
        onLogout={logout} 
      />
      
      <div className="home-layout">
        {/* 主内容区域 */}
        <div className="home-main-content">
          <Title level={2} className="home-title">最新文章</Title>
          
          {loading ? (
            <div className="loading-container">
              {[...Array(6)].map((_, index) => (
                <Card key={index} className="article-card" style={{ marginBottom: '24px' }}>
                  <Skeleton active paragraph={{ rows: 2 }} />
                </Card>
              ))}
            </div>
          ) : articles.length === 0 ? (
            <Empty 
              description="暂无文章"
              className="empty-state"
            />
          ) : (
            <List
              grid={{
                gutter: 24,
                xs: 1,
                sm: 1,
                md: 1,
                lg: 2,
                xl: 2,
                xxl: 2,
              }}
              dataSource={articles}
              renderItem={(article) => (
                <List.Item>
                  <Card 
                    className="article-card"
                    hoverable
                  >
                    <div className="article-card-header">
                      <Title 
                        level={4} 
                        className="article-card-title"
                        onClick={() => handleViewArticle(article)}
                      >
                        {article.title}
                      </Title>
                      <div className="article-card-meta">
                        <span className="meta-item">
                          <CalendarOutlined /> {formatDate(article.date)}
                        </span>
                        <span className="meta-item">
                          <UserOutlined /> {article.author}
                        </span>
                        <span className="meta-item">
                          <ClockCircleOutlined /> {calculateReadingTime(article.content)} 分钟阅读
                        </span>
                      </div>
                    </div>
                    
                    <div className="article-card-abstract">
                      <Text type="secondary">{article.abstract}</Text>
                    </div>
                    
                    <div className="article-card-tags">
                      {article.tags?.map((tag, index) => (
                        <Tag key={index} color="blue" className="article-tag">
                          {tag}
                        </Tag>
                      ))}
                    </div>
                    
                    <div className="article-card-footer">
                      <Button 
                        type="primary" 
                        onClick={() => handleViewArticle(article)}
                      >
                        阅读全文
                      </Button>
                    </div>
                  </Card>
                </List.Item>
              )}
            />
          )}
        </div>
        
        {/* 侧边栏 */}
        <div className="sidebar">
          <div className="sidebar-item">
            <Title level={4} className="sidebar-title">热门文章</Title>
            <List
              size="small"
              dataSource={getHotArticles()}
              renderItem={(article, index) => (
                <List.Item key={article.id}>
                  <div className="hot-article-item">
                    <span className="hot-article-rank">#{index + 1}</span>
                    <button 
                        className="hot-article-link"
                        onClick={() => handleViewArticle(article)}
                        aria-label={`查看文章: ${article.title}`}
                    >
                        {article.title}
                    </button>
                    </div>
                </List.Item>
              )}
            />
          </div>
          
          <div className="sidebar-item">
            <Title level={4} className="sidebar-title">标签云</Title>
            <Space wrap>
              {getAllTags().slice(0, 10).map((tag, index) => (
                <Tag key={index} color="blue" className="sidebar-tag">
                  {tag}
                </Tag>
              ))}
            </Space>
          </div>
          
          <div className="sidebar-item">
            <Title level={4} className="sidebar-title">关于博客</Title>
            <Text type="secondary">
              这是一个个人技术博客，分享编程经验、技术心得和生活感悟。
            </Text>
          </div>
        </div>
      </div>
      
      <AppFooter />
    </div>
  );
};

export default Home;